/**
 * The `sv_enforceGameBuild` ladder: which number pins a server to which DLC.
 *
 * `sv_enforceGameBuild` is the line that decides whether a resource streaming
 * content from a 2025 DLC works or silently loads nothing. It takes a build
 * number, and a server owner reading a raw number has no way to tell whether
 * `2802` is current or three years stale. The competitor's generator hard-codes
 * exactly that number into every config it writes, with no control anywhere in
 * its UI: a 2026 product pinning every new server to a December 2022 game build,
 * so a pack streaming anything newer loads nothing and the owner has no clue
 * why. This table exists so our dropdown can say `3889 · The Kortz Center Heist`
 * instead of `3889`, and so `cfg.build.stale` can say how far behind a number is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROVENANCE — this table is HAND-KEPT, and that is the honest label
 *
 * The plan for this file said "generated by `scripts/gta/build-vanilla-data.mjs`
 * rather than hand-kept", on the strength of the dump carrying 102 dated DLC
 * packs. It is not generated, for two reasons, and pretending otherwise would be
 * the exact dishonesty the rest of this layer is built to avoid:
 *
 *   1. The dump directory that generator reads is not on this machine. The
 *      committed outputs in `public/data/gta/` were produced in an earlier
 *      session against game build 1.0.3889.0; the dump itself was not committed
 *      (it is game content), so the generator cannot be re-run here.
 *   2. Even with the dump, it would only get half of this table. A DLC pack's
 *      `setup2.xml` gives its `mp*` pack name and its load order. It does not
 *      give the `sv_enforceGameBuild` number, which is a Cfx-side concept, and
 *      it does not give a release date. Those two columns have to come from
 *      somewhere else whatever the dumper does.
 *
 * So: build numbers and DLC names below are transcribed from the competitor's
 * own public build ladder, as captured from their game build filter and
 * corroborated by a second, independent capture of their build table. Both captures agree, and their top row
 * agrees with the build our own stock-name index was dumped from
 * (`1.0.3889.0`), which is the one cross-check available in this repo.
 *
 * That is a *secondary* source and it is labelled as one. The build numbers are
 * the load-bearing half — they are what `sv_enforceGameBuild` actually takes,
 * and they are what every rule here computes on. The DLC names are captions. If
 * a caption is wrong, the tool still writes the right number with a wrong label,
 * which is a cosmetic fault; a wrong number would be a server that will not
 * load its content. Verify captions against Rockstar's own release notes or the
 * `sv_enforceGameBuild` page in the FiveM docs before trusting one in prose.
 *
 * `released` is `null` on every row, and that is deliberate rather than pending
 * work nobody got to. No release date appears in either capture, the dump does
 * not carry one, and a date typed from memory next to a build number is exactly
 * the sort of confident-looking wrong fact this layer refuses to ship. Nothing
 * here needs a date: `gameBuildsBehind` measures staleness in releases, by
 * position in the ladder, which is both more useful ("four releases behind") and
 * checkable against the table itself.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW TO REGENERATE, when the dump is available
 *
 *   1. `node scripts/gta/dump-archives/run.mjs` against a local GTA V install.
 *      It already extracts `setup2.xml` and `content.xml` from every DLC pack.
 *   2. Read each pack's `setup2.xml` for its `<nameHash>` (`mp2026_01`) and its
 *      order, which gives the *ladder* — the sequence, and the pack names.
 *   3. Join that sequence onto the build numbers below. The numbers still have
 *      to come from Cfx: the ladder from the dump confirms the order and the
 *      pack names, it cannot invent `sv_enforceGameBuild` values.
 *   4. Add the join to `scripts/gta/build-vanilla-data.mjs` beside the modkit
 *      and spawn-name writers, emit this file from it, and replace this section
 *      with the generated-file banner the other `*.v1.ts` tables carry.
 *
 * Until step 4 lands, this file is hand-kept and says so. An honest hand-kept
 * table beats a generated-looking one that was never generated.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** One rung of the ladder. */
export type GameBuild = {
  /**
   * The value `sv_enforceGameBuild` takes, as a string.
   *
   * A string and not a number because that is what goes into a config file and
   * what comes back out of parsing one, and every round trip through a number
   * is a chance to print `2.802e3` or lose a leading zero somebody typed.
   */
  build: string;
  /** The DLC that shipped it, as the source above labels it. A caption. */
  dlc: string;
  /**
   * Release date, `YYYY-MM-DD`, or `null` when we have not verified one.
   *
   * Every row is `null` today. See the provenance note: a guessed date is worse
   * than a blank, and no rule in this layer reads this field.
   */
  released: string | null;
};

export const GAME_BUILDS_VERSION = 1;

export const GAME_BUILDS_SOURCE =
  "Hand-kept. Build numbers and DLC names transcribed from the competitor's public game-build filter, " +
  "captured twice, independently, with both captures agreeing; " +
  "release dates are not in either capture and are deliberately left blank rather than guessed";

export const GAME_BUILDS_UPDATED = "2026-09-11";

/**
 * The game build our own stock-asset index was dumped from.
 *
 * Kept here so the top of this ladder can be cross-checked against something in
 * this repo rather than only against the capture. It matches
 * `public/data/gta/vanilla-names-v1.json`, and if a future dump moves past
 * 1.0.3889.0 while this table's first row still says 3889, one of the two is out
 * of date and the mismatch is the signal.
 */
export const GAME_BUILDS_VERIFIED_AGAINST = "1.0.3889.0";

/** The one-sentence provenance line a report or a dropdown prints. */
export const GAME_BUILDS_STAMP =
  `game build ladder v${GAME_BUILDS_VERSION}, hand-kept, checked ${GAME_BUILDS_UPDATED} against ` +
  `GTA V build ${GAME_BUILDS_VERIFIED_AGAINST}; DLC names are captions from a secondary source and ` +
  `release dates are not recorded`;

/**
 * Every build, latest first.
 *
 * Latest first because that is the order a dropdown wants and the order the
 * staleness rule counts in, and because the newest row is the one a reader is
 * looking for. Index 0 is the current build; index n is n releases behind.
 *
 * The list starts at 1604. The captures carry an earlier `1 · Base game` row,
 * which is a sentinel their asset filter uses to mean "no DLC content at all"
 * and is not a value `sv_enforceGameBuild` accepts — writing `sv_enforceGameBuild 1`
 * into a config would be writing a number the server rejects, so it is not here.
 */
export const GAME_BUILDS: readonly GameBuild[] = [
  { build: "3889", dlc: "The Kortz Center Heist", released: null },
  { build: "3751", dlc: "A Safehouse in the Hills", released: null },
  { build: "3570", dlc: "Money Fronts", released: null },
  { build: "3407", dlc: "Agents of Sabotage", released: null },
  { build: "3258", dlc: "Bottom Dollar Bounties", released: null },
  { build: "3095", dlc: "The Chop Shop", released: null },
  { build: "2944", dlc: "San Andreas Mercenaries", released: null },
  { build: "2802", dlc: "Los Santos Drug Wars", released: null },
  { build: "2699", dlc: "The Criminal Enterprises", released: null },
  { build: "2612", dlc: "Expanded content", released: null },
  { build: "2545", dlc: "The Contract", released: null },
  { build: "2372", dlc: "Los Santos Tuners", released: null },
  { build: "2189", dlc: "Cayo Perico Heist", released: null },
  { build: "2060", dlc: "Los Santos Summer Special", released: null },
  { build: "1604", dlc: "Arena War", released: null },
];

/** Lower-cased lookup, built once. */
const BY_BUILD = new Map(GAME_BUILDS.map((row, at) => [row.build, { row, at }] as const));

/** The newest build in the table. */
export function latestGameBuild(): GameBuild {
  return GAME_BUILDS[0];
}

/**
 * The row for a build value, or `null`.
 *
 * Accepts a number or a string because a form control hands back one and a
 * parsed config hands back the other, and the only difference between them is
 * a `String()` call nobody should have to remember.
 */
export function gameBuild(value: string | number): GameBuild | null {
  return BY_BUILD.get(String(value).trim())?.row ?? null;
}

/** True when a value is a build this table knows. */
export function isKnownGameBuild(value: string | number): boolean {
  return BY_BUILD.has(String(value).trim());
}

/**
 * How many releases behind the latest a build is, or `null` when unknown.
 *
 * `null` and `0` are different answers and the caller must keep them apart: `0`
 * is "current", `null` is "this is not a number we have in the table", which
 * happens for a build newer than this file (someone updated their artifacts
 * before we updated this ladder) as well as for a typo. A rule that treated
 * `null` as stale would fire on exactly the servers that are most up to date.
 */
export function gameBuildsBehind(value: string | number): number | null {
  return BY_BUILD.get(String(value).trim())?.at ?? null;
}

/**
 * `3889 · The Kortz Center Heist`, or the bare value when we do not know it.
 *
 * The fallback is the raw value rather than "unknown build", because a config
 * that says `sv_enforceGameBuild 4012` is not necessarily wrong — it may simply
 * be newer than this file — and captioning it "unknown" reads as an accusation.
 */
export function gameBuildLabel(value: string | number): string {
  const text = String(value).trim();
  const row = BY_BUILD.get(text)?.row;
  return row ? `${row.build} · ${row.dlc}` : text;
}
